import { useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import type { GlobalSearchResult } from '../lib/globalSearch';
import { IS_UNIFIED_PORTAL, portalKindFromPath, type PortalKind } from '../lib/portal';
import { ensureSecondaryAuthSynced } from '../lib/supabase';

function needsSecondaryJwt(kind: PortalKind): boolean {
  return kind === 'dem' || kind === 'forms' || kind === 'it';
}

/** Soft-switch portal if needed, then open the search result path. */
export function useNavigateSearchResult() {
  const navigate = useNavigate();
  const { enterPortal, armPortalEntry } = useAuth();
  const busyRef = useRef(false);

  return useCallback(
    async (result: GlobalSearchResult) => {
      if (!IS_UNIFIED_PORTAL) {
        window.location.assign(result.href);
        return;
      }
      if (busyRef.current) return;
      busyRef.current = true;
      try {
        const currentPath = window.location.pathname;
        // Shared routes — no portal soft-switch required.
        if (
          result.href.startsWith('/calendar') ||
          result.href.startsWith('/admin') ||
          result.href === '/choose-portal'
        ) {
          navigate(result.href);
          return;
        }

        const currentPortal = portalKindFromPath(currentPath);
        if (currentPortal === result.portal) {
          navigate(result.href);
          return;
        }

        if (needsSecondaryJwt(result.portal)) {
          flushSync(() => {
            armPortalEntry(result.portal);
          });
          await ensureSecondaryAuthSynced(4_000);
          flushSync(() => {
            enterPortal(result.portal);
            navigate(result.href);
          });
        } else {
          flushSync(() => {
            enterPortal(result.portal);
            navigate(result.href);
          });
        }
      } finally {
        window.setTimeout(() => {
          busyRef.current = false;
        }, 0);
      }
    },
    [armPortalEntry, enterPortal, navigate],
  );
}
